import { unwrapListTextNodes, wrapListTextNodes } from '@src/components/sco/RichMarkdownInput/plugins/lists'
import {
  convertLinksToMentions,
  convertMentionsToLinks,
} from '@src/components/sco/RichMarkdownInput/plugins/mentions'
import remarkParse from 'remark-parse'
import remarkSlate, { defaultNodeTypes, serialize } from 'remark-slate'
import { Node as SlateNode } from 'slate'
import { unified } from 'unified'

/**
 * Deserialize a list of nodes from markdownString
 * @param markdownString
 */
export const deserializeNodesFromMarkdown = (markdownString: any): SlateNode[] => {
  let value = null

  try {
    // @ts-ignore
    const parsed = unified()
      .use(remarkParse)
      .use(remarkSlate, { linkDestinationKey: 'url' })
      .processSync(markdownString)

    value = parsed.result

    // convert links to mentions
    value = convertLinksToMentions(value)
    value = wrapListTextNodes(value)
    value = correctParagraphBreaks(value)
  } catch (err) {
    console.log(err)
    return [{ children: [{ text: '!!MARKDOWNINPUT DE-SERIALIZATION ERROR!!' }] }]
  }

  if (!value || value?.length == 0 || Object.keys(value).length == 0) {
    value = [{ type: 'paragraph', children: [{ text: '' }] }]
  }

  return value
}

export const removeEmptyTextSiblings = (nodes: any) => {
  return nodes
    .map((n: any) => {
      if (!n.type && !n.text) {
        return null
      }
      if (n.children && n.children.length > 1) {
        return {
          ...n,
          children: removeEmptyTextSiblings(n.children),
        }
      }
      return n
    })
    .filter(Boolean)
}

export const preprocessNodesForMarkdown = (nodes: any): string => {
  let processedNodes: any = nodes

  processedNodes = removeEmptyTextSiblings(processedNodes)
  processedNodes = convertMentionsToLinks(processedNodes)
  processedNodes = unwrapListTextNodes(processedNodes)

  // Copy url property to link property for all link nodes
  const addLinkProperty = (node: any): any => {
    if (node.type === 'link' && node.url) {
      return {
        ...node,
        link: node.url,
        children: Array.isArray(node.children) ? node.children.map(addLinkProperty) : [],
      }
    } else if (Array.isArray(node.children)) {
      return {
        ...node,
        children: node.children.map(addLinkProperty),
      }
    }
    return node
  }

  processedNodes = processedNodes.map(addLinkProperty)

  return processedNodes
}

export const serializeNodesToMarkdown = (nodes: any): string => {
  let markdown = ''

  const processedNodes: any = preprocessNodesForMarkdown(nodes)

  try {
    // Generate the raw markdown string
    markdown = processedNodes
      .map(slateNode => {
        return serialize(slateNode, {
          ignoreParagraphNewline: false,
          nodeTypes: defaultNodeTypes,
        })
      })
      .join('')

    //Replace repeating spaces with &nbsp;
    // markdown = markdown.replaceAll('  ', '&nbsp;&nbsp;')
  } catch (err) {
    console.log(err)
    markdown = '!!MARKDOWNINPUT SERIALIZATION ERROR!!'
  }

  if (markdown === '<br>\n\n') {
    markdown = ''
  }
  return markdown
}

export const correctParagraphBreaks = (nodes: any[]): Array<any> => {
  const newNodes = []
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if (node.children && node.children.length > 0) {
      const lastChild = node.children[node.children.length - 1]
      const prevChild = node.children[node.children.length - 2]

      if (
        lastChild.break &&
        typeof prevChild?.text === 'string' &&
        (prevChild.text.endsWith('\n') || prevChild.text.trim().length === 0)
      ) {
        // Remove the trailing newline from the previous child
        prevChild.text = prevChild.text.endsWith('\n') ? prevChild.text.slice(0, -1) : prevChild.text

        // Create new node structure without the break child
        const newNode = {
          ...node,
          children: node.children.slice(0, prevChild.text.trim().length === 0 ? -2 : -1),
        }

        // Add the modified node and the break as siblings
        newNodes.push(newNode)
        newNodes.push(lastChild)
      } else {
        // Recursively process children
        newNodes.push({
          ...node,
          children: correctParagraphBreaks(node.children),
        })
      }
    } else {
      newNodes.push(node)
    }
  }
  return newNodes
}
